import { ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { useEffect, useRef } from 'react';
import { trackPageView, trackPaymentClick, trackQRView } from '../utils/analytics';
import SiteHeader from './SiteHeader';
import SiteFooter from './SiteFooter';

const Payment = () => {
  const venmoLink = 'https://venmo.com/code?user_id=4484655516485443999&created=1765380663.6551008&printed=1';
  const qrCodeRef = useRef(null);
  const qrTracked = useRef(false);

  // TOGGLE THIS: Set to true to blur the QR/pay button (e.g. when tickets sold out)
  const isPaymentDisabled = false;

  useEffect(() => {
    trackPageView('Payment');
  }, []);

  // Track QR view once it scrolls into view
  useEffect(() => {
    const node = qrCodeRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !qrTracked.current) {
            trackQRView();
            qrTracked.current = true;
          }
        });
      },
      { threshold: 0.5 }
    );
    observer.observe(node);
    return () => observer.unobserve(node);
  }, []);

  const handlePaymentClick = () => {
    trackPaymentClick();
    window.open(venmoLink, '_blank');
  };

  return (
    <div className="min-h-screen bg-cream-50" data-testid="payment-page">
      <SiteHeader active="payment" />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#1a0d10] text-white py-20 md:py-28 px-4 md:px-6">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(124,29,51,0.22),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(139,30,63,0.16),transparent_60%)]" />
        <div className="absolute inset-0 grain" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <p className="eyebrow text-white/50 mb-5">Secure Your Spot</p>
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl font-semibold leading-[0.95] tracking-tighter mb-6">
            <span className="block">Retreat</span>
            <span className="block bg-gradient-to-br from-[#a8324a] via-[#c0455c] to-[#d9a15b] bg-clip-text text-transparent">
              Payment
            </span>
          </h2>
          <p className="text-sm md:text-base text-white/70 max-w-2xl mx-auto leading-relaxed">
            Only pay after you&apos;ve completed the registration form AND received a confirmation email
            letting you know a spot has opened for you.
          </p>
        </div>
      </section>

      {/* Waitlist warning banner */}
      <section className="py-12 md:py-14 px-4 md:px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-6 md:p-8 text-left space-y-3">
            <p className="text-lg md:text-xl font-bold text-amber-900">
              Joining the waitlist? Do NOT pay yet.
            </p>
            <p className="text-sm md:text-base text-amber-900 leading-relaxed">
              Fill out the registration form first. <span className="font-bold">Only submit payment AFTER you receive a registration confirmation email</span> letting you know a spot has opened up for you. If you pay before that, we&apos;ll refund you — but it delays everyone.
            </p>
            <p className="text-sm md:text-base text-amber-900 leading-relaxed">
              <span className="font-bold">Please note:</span> Do not submit payment for anyone who has not completed the registration form. If you are paying for multiple attendees, each person must submit their own registration form before payment is made.
            </p>
          </div>
        </div>
      </section>

      {/* Payment info + QR */}
      <section className="py-12 md:py-16 px-4 md:px-6 bg-cream-100 border-y border-cream-200">
        <div className="max-w-6xl mx-auto">
          <div className="relative">
            {isPaymentDisabled && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/10 backdrop-blur-md rounded-xl">
                <div className="bg-wine text-white px-8 py-4 rounded-lg shadow-2xl text-center">
                  <p className="text-2xl font-bold mb-2">Payment Currently Unavailable</p>
                  <p className="text-sm">Join the waitlist first</p>
                </div>
              </div>
            )}

            <div className={isPaymentDisabled ? 'pointer-events-none select-none' : ''}>
              <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
                {/* Pricing */}
                <div className="space-y-6 md:space-y-8">
                  <Card className="border-2 border-wine/30 shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 md:p-8">
                      <div className="flex items-start gap-3 md:gap-4">
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-wine to-burgundy rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-lg md:text-xl">1</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-xl md:text-2xl font-bold mb-2 text-gray-900">Initial Deposit</h4>
                          <p className="text-3xl md:text-4xl font-bold text-wine mb-3">$175</p>
                          <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                            Secure your spot with a deposit. Pay the remaining balance before the retreat.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-wine/30 shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 md:p-8">
                      <div className="flex items-start gap-3 md:gap-4">
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-wine to-burgundy rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-lg md:text-xl">2</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-xl md:text-2xl font-bold mb-2 text-gray-900">Base Ticket Price</h4>
                          <p className="text-3xl md:text-4xl font-bold text-wine mb-3">$325</p>
                          <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                            Pay in full now and you&apos;re all set for the retreat.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="bg-white border-l-4 border-wine p-4 md:p-6 rounded-lg space-y-3 shadow-sm">
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                      <span className="font-semibold text-gray-900">Note:</span> For single or double room accommodations, individuals will be contacted with their specific rate.
                    </p>
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                      <span className="font-semibold text-gray-900">Important:</span> No pets of any kind are permitted at the retreat.
                    </p>
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                      <span className="font-semibold text-gray-900">Alternative Payment:</span> If you prefer to pay via check, please email{' '}
                      <a href="mailto:info@rojoretreat.com" className="text-wine hover:text-wine-700 underline font-medium break-all">
                        info@rojoretreat.com
                      </a>
                    </p>
                  </div>
                </div>

                {/* QR code */}
                <div className="text-center" ref={qrCodeRef}>
                  <div className="inline-block bg-white p-6 md:p-8 rounded-2xl shadow-xl border-4 border-wine/20">
                    <img
                      src="/venmo-qr.png"
                      alt="Venmo QR Code"
                      className="w-48 h-48 md:w-64 md:h-64 object-contain mx-auto"
                    />
                  </div>
                  <div className="mt-6 md:mt-8">
                    <Button
                      onClick={handlePaymentClick}
                      size="lg"
                      className="bg-gradient-to-r from-wine to-burgundy hover:from-wine-600 hover:to-burgundy-600 text-white font-semibold px-8 md:px-10 py-4 md:py-6 text-base md:text-lg"
                      data-testid="payment-page-pay-btn"
                    >
                      Click to Pay
                      <ExternalLink className="ml-2 w-4 h-4 md:w-5 md:h-5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Reservation notice */}
          <div className="mt-8 md:mt-12">
            <div className="bg-wine-50 border-2 border-wine/30 rounded-xl p-6 md:p-8 text-center space-y-4">
              <p className="text-xl md:text-2xl font-bold text-wine-700">
                Only 140 Tickets Available
              </p>
              <p className="text-base md:text-lg text-gray-800 leading-relaxed">
                Tickets are reserved strictly in the order that both the registration form and payment are received. Submitting the form alone does not reserve a spot. Your registration is not guaranteed until you receive a confirmation email. A deposit will hold your place in line pending confirmation.
              </p>
              <p className="text-base md:text-lg text-gray-800 leading-relaxed">
                <span className="font-bold text-gray-900">Please note:</span> Do not submit payment for anyone who has not completed the registration form. If you are paying for multiple attendees, each person must submit their own registration form before payment is made.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default Payment;
